import React, { useEffect, useState, useContext } from "react";
import { Pie, Bar, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
} from "chart.js";
import { ThemeContext } from '../context/ThemeContext';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement
);

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [weeklyMetrics, setWeeklyMetrics] = useState(null);
  const [monthlyMetrics, setMonthlyMetrics] = useState(null);
  const [yearlyMetrics, setYearlyMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questionsSummary, setQuestionsSummary] = useState(null);
  const { mode, customColors } = useContext(ThemeContext);
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullName = sessionStorage.getItem("fullName") || "User";

  // Gradient colors for charts and UI
  const gradientPrimary = "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)";
  const gradientLight = "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)";
  const gradientDark = "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)";

  // Dynamic text and background colors
  const textColor = mode === "dark" ? "#E0E0E0" : mode === "custom" ? customColors?.text || "#222222" : "#333333";
  const cardBg = mode === "dark" ? gradientDark : mode === "custom" ? customColors?.dashboardBg || gradientLight : gradientLight;
  const tooltipBgColor = mode === "dark" ? "#2A2A3C" : mode === "custom" ? customColors?.cardBg || "#ffffffcc" : "#ffffff";

  // Chart colors
  const chartColors = {
    primary: "#1e40af",
    secondary: "#3b82f6",
    accent: "#60a5fa",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    info: "#06b6d4",
  };

  // MonthlyActivityHeatmap Component
  const MonthlyActivityHeatmap = ({ monthlyMetrics, mode = "light", customColors = {} }) => {
    const [hoveredDay, setHoveredDay] = useState(null);

    const textColor = mode === "dark" ? "#E0E0E0" : mode === "custom" ? customColors?.text || "#222222" : "#333333";
    const cardBg = mode === "dark"
      ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
      : mode === "custom"
        ? customColors?.dashboardBg || "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
        : "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)";

    const dailyActivity = monthlyMetrics?.daily_activity || [];

    // Get color based on review count
    const getHeatmapColor = (reviews) => {
      if (reviews === 0) return mode === "dark" ? "#1e293b" : "#e2e8f0";
      if (reviews <= 5) return "#bfdbfe";
      if (reviews <= 10) return "#60a5fa";
      if (reviews <= 20) return "#3b82f6";
      return "#1e40af";
    };

    // Get intensity label
    const getIntensityLabel = (reviews) => {
      if (reviews === 0) return "No activity";
      if (reviews <= 5) return "Light activity";
      if (reviews <= 10) return "Moderate activity";
      if (reviews <= 20) return "High activity";
      return "Very high activity";
    };

    // Organize data by weeks
    const organizeByWeeks = () => {
      if (!dailyActivity.length) return [];

      const weeks = [];
      let currentWeek = [];

      dailyActivity.forEach((day, index) => {
        const date = new Date(day.date);
        const dayOfWeek = date.getDay();

        if (index === 0) {
          for (let i = 0; i < (dayOfWeek === 0 ? 6 : dayOfWeek - 1); i++) {
            currentWeek.push(null);
          }
        }

        currentWeek.push(day);

        if (dayOfWeek === 0 || index === dailyActivity.length - 1) {
          if (index === dailyActivity.length - 1 && dayOfWeek !== 0) {
            const remainingDays = 7 - (dayOfWeek === 0 ? 7 : dayOfWeek);
            for (let i = 0; i < remainingDays; i++) {
              currentWeek.push(null);
            }
          }
          weeks.push([...currentWeek]);
          currentWeek = [];
        }
      });

      return weeks;
    };

    const weeks = organizeByWeeks();
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Calculate stats
    const totalReviews = dailyActivity.reduce((sum, day) => sum + day.reviews, 0);
    const activeDays = dailyActivity.filter(day => day.reviews > 0).length;
    const avgPerActiveDay = activeDays > 0 ? (totalReviews / activeDays).toFixed(1) : 0;
    const maxReviews = Math.max(...dailyActivity.map(day => day.reviews));
    const bestDay = dailyActivity.find(day => day.reviews === maxReviews);

    return (
      <Card
        sx={{
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          borderRadius: 4,
          p: 3,
          height: "100%",
          transition: "all 0.3s ease",
          "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
          background: cardBg,
          border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ color: textColor, fontFamily: "'Inter', sans-serif", mb: 1 }}>
            Monthly Activity Calendar
          </Typography>
          <Typography variant="body2" sx={{ color: textColor, opacity: 0.7, fontFamily: "'Inter', sans-serif", mb: 3 }}>
            {activeDays} active days • {avgPerActiveDay} avg reviews per active day
          </Typography>

          {/* Calendar Grid */}
          <Box sx={{ mb: 3 }}>
            {/* Day labels */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, ml: '40px' }}>
              {weekDays.map((day) => (
                <Box
                  key={day}
                  sx={{
                    width: 28,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: textColor,
                      opacity: 0.6,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Weeks */}
            {weeks.map((week, weekIndex) => (
              <Box key={weekIndex} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: textColor,
                    opacity: 0.5,
                    fontFamily: "'Inter', sans-serif",
                    minWidth: '35px',
                    textAlign: 'right',
                    mr: 0.5,
                  }}
                >
                  W{weekIndex + 1}
                </Typography>

                {week.map((day, dayIndex) => {
                  if (!day) {
                    return (
                      <Box
                        key={dayIndex}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          backgroundColor: 'transparent',
                        }}
                      />
                    );
                  }

                  const date = new Date(day.date);
                  const dateStr = date.getDate();
                  const isHovered = hoveredDay === day.date;

                  return (
                    <Box
                      key={dayIndex}
                      sx={{ position: 'relative', width: 28, height: 28 }}
                      onMouseEnter={() => setHoveredDay(day.date)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          backgroundColor: getHeatmapColor(day.reviews),
                          border: day.reviews > 0 ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: isHovered ? 'scale(1.15)' : 'scale(1)',
                          boxShadow: isHovered ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
                          zIndex: isHovered ? 10 : 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: day.reviews > 10 ? '#fff' : textColor,
                            opacity: day.reviews > 0 ? 1 : 0.3,
                          }}
                        >
                          {dateStr}
                        </Typography>
                      </Box>

                      {isHovered && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: '35px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: mode === "dark" ? '#2A2A3C' : '#ffffff',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            whiteSpace: 'nowrap',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textColor, display: 'block' }}>
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', mt: 0.5, color: textColor, opacity: 0.9, display: 'block' }}>
                            {day.reviews} reviews
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: textColor, opacity: 0.7, display: 'block' }}>
                            {getIntensityLabel(day.reviews)}
                          </Typography>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: '-6px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 0,
                              height: 0,
                              borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent',
                              borderTop: `6px solid ${mode === "dark" ? '#2A2A3C' : '#ffffff'}`,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}

            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: textColor, opacity: 0.6, mr: 1 }}>
                  Less
                </Typography>
                {[0, 5, 10, 20, 25].map((level) => (
                  <Box
                    key={level}
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 0.5,
                      backgroundColor: getHeatmapColor(level),
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                  />
                ))}
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: textColor, opacity: 0.6, ml: 1 }}>
                  More
                </Typography>
              </Box>

              {bestDay && (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: textColor, opacity: 0.7, fontFamily: "'Inter', sans-serif" }}>
                  🔥 Best day: {new Date(bestDay.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ({maxReviews} reviews)
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const fetchData = async () => {
      try {
        const dailyResponse = await fetch(`${apiUrl}/daily-summary/`, { headers: { Authorization: `Bearer ${token}` } });
        const dailyData = await dailyResponse.json();
        setMetrics(dailyData);

        const weeklyResponse = await fetch(`${apiUrl}/weekly-summary/`, { headers: { Authorization: `Bearer ${token}` } });
        const weeklyData = await weeklyResponse.json();
        setWeeklyMetrics(weeklyData);

        const monthlyResponse = await fetch(`${apiUrl}/monthly-summary/`, { headers: { Authorization: `Bearer ${token}` } });
        const monthlyData = await monthlyResponse.json();
        setMonthlyMetrics(monthlyData);

        const yearlyResponse = await fetch(`${apiUrl}/yearly-summary/`, { headers: { Authorization: `Bearer ${token}` } });
        const yearlyData = await yearlyResponse.json();
        setYearlyMetrics(yearlyData);

        const questionsResponse = await fetch(`${apiUrl}/questions/summary/`, { headers: { Authorization: `Bearer ${token}` } });
        const questionsData = await questionsResponse.json();
        setQuestionsSummary(questionsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Data processing
  const performanceMetrics = metrics?.performance_metrics || {
    "easily recalled": 0,
    "partially recalled": 0,
    forgot: 0,
    skip: 0,
    "recalled with effort": 0,
  };

  const yearlyPerformanceMetrics = yearlyMetrics?.overall_performance_metrics || {
    "easily recalled": 0,
    "partially recalled": 0,
    forgot: 0,
    skip: 0,
    "recalled with effort": 0,
  };

  const flashcardsByType = questionsSummary?.by_type || {
    MCQ: 0,
    FIB: 0,
    //SUB: 0,
    TRUEFALSE: 0,
    IMAGE: 0,
  };

  // Chart data
  const yearlyBarData = {
    labels: ["Easily Recalled", "Partially Recalled", "Forgot", "Skipped", "Recalled with Effort"],
    datasets: [{
      label: "Yearly Performance",
      data: [
        yearlyPerformanceMetrics["easily recalled"],
        yearlyPerformanceMetrics["partially recalled"],
        yearlyPerformanceMetrics["forgot"],
        yearlyPerformanceMetrics["skip"],
        yearlyPerformanceMetrics["recalled with effort"],
      ],
      backgroundColor: [
        chartColors.success,
        chartColors.info,
        chartColors.danger,
        chartColors.warning,
        chartColors.secondary,
      ],
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const yearlyLineData = {
    labels: (yearlyMetrics?.monthly_summary || []).map((item) =>
      new Date(item.month).toLocaleString("en-US", { month: "short", year: "numeric" })
    ),
    datasets: [{
      label: "Monthly Reviews",
      data: (yearlyMetrics?.monthly_summary || []).map((item) => item.total_reviews),
      borderColor: chartColors.primary,
      backgroundColor: `${chartColors.primary}33`,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: chartColors.primary,
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: chartColors.primary,
    }],
  };

  const getStartOfWeek = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };

  const currentWeekStart = getStartOfWeek(new Date());
  const today = new Date();

  const weeklyPerformanceBase = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + index);
    return {
      day: date.toLocaleDateString("en-US", { weekday: "short" }),
      date: date.toISOString().split("T")[0],
      progress: 0,
    };
  });

  const weeklyPerformance = weeklyPerformanceBase.map((item) => {
    const matchingData = weeklyMetrics?.daily_activity?.find((activity) => activity.date === item.date);
    return { ...item, progress: matchingData ? matchingData.reviews : 0 };
  });

  const weeklyLineData = {
    labels: weeklyPerformance.filter((item) => new Date(item.date) <= today).map((item) => item.day),
    datasets: [{
      label: "Weekly Progress",
      data: weeklyPerformance.filter((item) => new Date(item.date) <= today).map((item) => item.progress),
      borderColor: chartColors.accent,
      backgroundColor: `${chartColors.accent}33`,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: chartColors.accent,
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: chartColors.accent,
    }],
  };

  const barData = {
    labels: ["Easily Recalled", "Partially Recalled", "Forgot", "Skipped", "Recalled with Effort"],
    datasets: [{
      label: "Feedback Distribution",
      data: [
        (monthlyMetrics?.feedback_distribution || []).find((item) => item.feedback_type === "easily recalled")?.count || 0,
        (monthlyMetrics?.feedback_distribution || []).find((item) => item.feedback_type === "partially recalled")?.count || 0,
        (monthlyMetrics?.feedback_distribution || []).find((item) => item.feedback_type === "forgot")?.count || 0,
        (monthlyMetrics?.feedback_distribution || []).find((item) => item.feedback_type === "skip")?.count || 0,
        (monthlyMetrics?.feedback_distribution || []).find((item) => item.feedback_type === "recalled with effort")?.count || 0,
      ],
      backgroundColor: [
        chartColors.success,
        chartColors.info,
        chartColors.danger,
        chartColors.warning,
        chartColors.secondary,
      ],
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const pieData = {
    labels: ["Easily Recalled", "Partially Recalled", "Forgot", "Skipped", "Recalled with Effort"],
    datasets: [{
      data: [
        performanceMetrics["easily recalled"],
        performanceMetrics["partially recalled"],
        performanceMetrics["forgot"],
        performanceMetrics["skip"],
        performanceMetrics["recalled with effort"],
      ],
      backgroundColor: [
        chartColors.success,
        chartColors.info,
        chartColors.danger,
        chartColors.warning,
        chartColors.secondary,
      ],
      borderWidth: 1,
    }],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12, family: "'Inter', sans-serif" },
          color: textColor,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: tooltipBgColor,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: mode === "dark" ? "#555" : "#ccc",
        borderWidth: 1,
        padding: 12,
        bodyFont: { family: "'Inter', sans-serif" },
        callbacks: { label: (context) => `${context.label}: ${context.raw}` },
      },
    },
    scales: {
      x: { grid: { color: mode === "dark" ? "#444" : "#E0E0E0" }, ticks: { color: textColor } },
      y: { grid: { color: mode === "dark" ? "#444" : "#E0E0E0" }, ticks: { color: textColor } },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          font: { size: 12, family: "'Inter', sans-serif" },
          color: textColor,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: tooltipBgColor,
        titleColor: textColor,
        bodyColor: textColor,
        padding: 12,
        bodyFont: { family: "'Inter', sans-serif" },
        callbacks: { label: (context) => `${context.label}: ${context.raw}` },
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', p: isMobile ? 2 : 4, background: mode === "dark" ? "transparent" : mode === "custom" ? "transparent" || "#f8fafc" : "#F5F5F5" }}>
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h3"
          fontWeight="700"
          sx={{
            mb: 1,
            fontFamily: "'Inter', sans-serif",
            color: textColor,
            background: mode === "dark" ? gradientLight : mode === "custom" ? "#f5f5f5" || "#222222" : gradientPrimary,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Hey Champ! Your Progress Overview
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: textColor,
            opacity: 0.8,
          }}
        >
          Track your learning journey and stay on top of your progress.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress size={40} sx={{ color: chartColors.primary }} />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3} maxWidth={1200} mx="auto">
          {/* Flashcards by Type */}
          <Card
            sx={{
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
              borderRadius: 4,
              p: 3,
              transition: "all 0.3s ease",
              "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
              background: cardBg,
              border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ fontFamily: "'Inter', sans-serif", color: textColor }}>
                Flashcards by Type
              </Typography>
              <Divider sx={{ my: 2, borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)" }} />
              <Grid container spacing={2}>
               {Object.entries(flashcardsByType)
  // .filter(([key]) => key !== "SUB")   // ❌ OLD (commented)
  .filter(([key]) => key !== "SUB")     // ✅ SUB HIDDEN
  .map(([key, value]) => (
    <Grid item xs={6} sm={4} md={2.4} key={key}>
      <Box
        textAlign="center"
        p={2}
        borderRadius={2}
        sx={{
          background:
            mode === "dark"
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.02)",
        }}
      >
        <Typography variant="subtitle2" fontWeight={500}>
          {key}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </Box>
    </Grid>
  ))}

              </Grid>
            </CardContent>
          </Card>

          {/* Performance Summaries */}
          <Box>
            <Typography variant="h5" fontWeight="600" gutterBottom sx={{ fontFamily: "'Inter', sans-serif", color: textColor, mb: 2 }}>
              Performance Summaries
            </Typography>
            <Grid container spacing={3}>
              {/* Daily Performance */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    borderRadius: 4,
                    p: 3,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
                    background: cardBg,
                    border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" sx={{ color: textColor, fontFamily: "'Inter', sans-serif" }}>
                      Daily Performance
                    </Typography>
                    <Typography variant="body2" mt={1} sx={{ color: textColor, opacity: 0.8, fontFamily: "'Inter', sans-serif" }}>
                      Total Cards Reviewed: <strong>{metrics?.total_cards_reviewed || 0}</strong>
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)" }} />
                    <Box sx={{ height: 300, width: "100%" }}>
                      <Pie data={pieData} options={pieOptions} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Weekly Performance */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    borderRadius: 4,
                    p: 3,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
                    background: cardBg,
                    border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" sx={{ color: textColor, fontFamily: "'Inter', sans-serif" }}>
                      Weekly Performance
                    </Typography>
                    <Typography variant="body2" mb={2} sx={{ color: textColor, opacity: 0.8, fontFamily: "'Inter', sans-serif" }}>
                      Total Cards Reviewed: <strong>{weeklyMetrics?.total_cards_reviewed || 0}</strong>
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)" }} />
                    <Box sx={{ height: 250, width: "100%" }}>
                      <Line data={weeklyLineData} options={chartOptions} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Monthly Performance */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    borderRadius: 4,
                    p: 3,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
                    background: cardBg,
                    border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" sx={{ color: textColor, fontFamily: "'Inter', sans-serif" }}>
                      Monthly Performance
                    </Typography>
                    <Typography variant="body2" mb={2} sx={{ color: textColor, opacity: 0.8, fontFamily: "'Inter', sans-serif" }}>
                      Total Cards Reviewed: <strong>{monthlyMetrics?.total_cards_reviewed || 0}</strong>
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)" }} />
                    <Box sx={{ height: 250, width: "100%" }}>
                      <Bar data={barData} options={chartOptions} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Monthly Activity Heatmap */}
              <Grid item xs={12} md={6}>
                <MonthlyActivityHeatmap
                  monthlyMetrics={monthlyMetrics}
                  mode={mode}
                  customColors={customColors}
                />
              </Grid>

              {/* Yearly Performance */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    borderRadius: 4,
                    p: 3,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
                    background: cardBg,
                    border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" sx={{ color: textColor, fontFamily: "'Inter', sans-serif" }}>
                      Yearly Performance
                    </Typography>
                    <Typography variant="body2" mb={2} sx={{ color: textColor, opacity: 0.8, fontFamily: "'Inter', sans-serif" }}>
                      Total Cards Reviewed: <strong>{yearlyMetrics?.total_cards_reviewed || 0}</strong>
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)" }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="500" sx={{ color: textColor, fontFamily: "'Inter', sans-serif", mb: 1 }}>
                          Overall Performance
                        </Typography>
                        <Box sx={{ height: 200, width: "100%" }}>
                          <Bar data={yearlyBarData} options={chartOptions} />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="500" sx={{ color: textColor, fontFamily: "'Inter', sans-serif", mb: 1, mt: 2 }}>
                          Monthly Trends
                        </Typography>
                        <Box sx={{ height: 200, width: "100%" }}>
                          <Line data={yearlyLineData} options={chartOptions} />
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
